import React, { useEffect, useState } from 'react';

/**
 * PWAUpdatePrompt
 * Renders a modal when a new service worker version is available.
 * Accepts the `registration` prop from the SW onUpdate callback.
 */
const PWAUpdatePrompt = ({ registration, onDismiss }) => {
  const [updating, setUpdating] = useState(false);

  const handleUpdate = () => {
    if (!registration || !registration.waiting) return;
    setUpdating(true);
    // Tell the waiting SW to skip waiting and become active
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  useEffect(() => {
    if (!registration) return;
    // When the new SW takes control, reload the page to load fresh assets
    const handleControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [registration]);

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 9999 }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        style={{ maxWidth: '430px' }}
      >
        <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
          {/* Header */}
          <div
            className="px-4 pt-4 pb-3"
            style={{
              background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <i className="ti ti-refresh fs-22 text-white" />
              </div>
              <div>
                <h5 className="fw-bold text-white mb-0">Update Available</h5>
                <p className="fs-13 mb-0" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  A new version of the app is ready
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body px-4 py-4">
            <p className="text-muted fs-14 mb-3" style={{ lineHeight: 1.65 }}>
              A new version of <strong className="text-dark">PMS Celebration</strong> has been
              deployed. Update now to get the latest features, improvements, and bug fixes.
            </p>
            <div
              className="rounded-3 p-3 d-flex align-items-start gap-2"
              style={{ backgroundColor: '#f0f0ff', border: '1px solid #d4d4ff' }}
            >
              <i className="ti ti-info-circle text-primary flex-shrink-0 mt-1" />
              <p className="fs-13 text-muted mb-0" style={{ lineHeight: 1.6 }}>
                The page will reload automatically after the update is applied.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            className="modal-footer border-0 px-4 pb-4 pt-0 gap-2"
            style={{ justifyContent: 'stretch' }}
          >
            <button
              type="button"
              className="btn btn-light border flex-grow-1 fw-medium fs-14"
              onClick={onDismiss}
              disabled={updating}
            >
              Later
            </button>
            <button
              type="button"
              className="btn flex-grow-1 fw-bold fs-14 d-flex align-items-center justify-content-center gap-2"
              onClick={handleUpdate}
              disabled={updating}
              style={{
                background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                border: 'none',
                color: '#fff',
              }}
            >
              {updating ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <i className="ti ti-refresh fs-16" />
                  <span>Update Now</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * usePWAUpdate hook
 * Returns { showPrompt, registration, dismiss } for use in App.js.
 */
export const usePWAUpdate = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const handleUpdate = (event) => {
      setRegistration(event.detail.registration);
      setShowPrompt(true);
    };

    window.addEventListener('pwa-update-available', handleUpdate);
    return () => {
      window.removeEventListener('pwa-update-available', handleUpdate);
    };
  }, []);

  const dismiss = () => setShowPrompt(false);

  return { showPrompt, registration, dismiss };
};

export default PWAUpdatePrompt;
